import type { Permission } from "../constants/permissions";

import {
  CONTACT_VISIBILITY,
  type ContactVisibility,
  type PermissionCatalog,
} from "@/types/role";
import { WORKSPACE_ROLE, type WorkspaceRole } from "@/types/user";

// The backend's rules for a role's keys, as `roles/catalog` sends them: which
// keys exist, what each needs, what an Agent or a limited role can't hold and
// what each type starts with. The server applies the same rules on save.
export class PermissionRules {
  readonly keys: Permission[];
  private readonly requires: Map<Permission, Permission[]>;

  constructor(private readonly catalog: PermissionCatalog) {
    this.keys = catalog.permissions.map(({ key }) => key);
    this.requires = new Map(
      catalog.permissions.map(({ key, requires }) => [key, requires]),
    );
  }

  // Everything a key needs, directly or through what it needs, nearest first.
  ancestors(key: Permission): Permission[] {
    const needed = this.requires.get(key) ?? [];

    return [
      ...new Set([
        ...needed,
        ...needed.flatMap((item) => this.ancestors(item)),
      ]),
    ];
  }

  // Everything past conversation work for an Agent, what a limited role can't
  // hold (workspace-wide screens, teams, integrations), and everything under either.
  blocked(
    base: WorkspaceRole,
    contactVisibility: ContactVisibility,
  ): Permission[] {
    return [
      ...(base === WORKSPACE_ROLE.AGENT ? this.catalog.withheldFromAgents : []),
      ...(contactVisibility === CONTACT_VISIBILITY.ALL
        ? []
        : this.catalog.workspaceWide),
    ].flatMap((key) => [key, ...this.descendants(key)]);
  }

  // What the editor lists for a role of this type: an Agent's never shows
  // what it can't hold.
  offered(base: WorkspaceRole): Permission[] {
    const withheld = this.blocked(base, CONTACT_VISIBILITY.ALL);

    return this.keys.filter((key) => !withheld.includes(key));
  }

  // Ticking one ticks what it needs; unticking clears what depends on it.
  tick(permissions: Permission[], key: Permission): Permission[] {
    return [
      ...permissions,
      ...[key, ...this.ancestors(key)].filter(
        (item) => !permissions.includes(item),
      ),
    ];
  }

  untick(permissions: Permission[], key: Permission): Permission[] {
    const dropped = [key, ...this.descendants(key)];

    return permissions.filter((item) => !dropped.includes(item));
  }

  // Puts `keys` back the way `baseline` holds them, with what each needs or
  // what needs it.
  restore(
    permissions: Permission[],
    baseline: Permission[],
    keys: Permission[],
  ): Permission[] {
    return keys.reduce(
      (held, key) =>
        baseline.includes(key) ? this.tick(held, key) : this.untick(held, key),
      permissions,
    );
  }

  // Drops unknown keys, keys whose requirement is missing, and what `blocked`
  // keeps off this role.
  normalize(
    permissions: readonly string[],
    base: WorkspaceRole,
    contactVisibility: ContactVisibility = CONTACT_VISIBILITY.ALL,
  ): Permission[] {
    const given = new Set(permissions);
    const blocked = this.blocked(base, contactVisibility);
    const holds = (key: Permission): boolean =>
      given.has(key) &&
      !blocked.includes(key) &&
      (this.requires.get(key) ?? []).every(holds);

    return this.keys.filter(holds);
  }

  preset(base: WorkspaceRole): Permission[] {
    return [...(this.catalog.presets[base] ?? [])];
  }

  private descendants(key: Permission): Permission[] {
    return this.keys.filter((item) => this.ancestors(item).includes(key));
  }
}
